import React from 'react';
import { Box, Container, Grid, Stack, Typography, Button, Paper } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import FeatureCard from '../components/FeatureCard';
import CTASection from '../components/CTASection';
import conciergeAiImage from '../assets/concierge_ai.png';
import { brand } from '../theme';

const PILLARS = [
  {
    icon: CodeRoundedIcon,
    title: 'Build',
    description:
      'Custom software shaped to how your business actually runs — not a one-size-fits-all SaaS.',
  },
  {
    icon: AutoAwesomeRoundedIcon,
    title: 'Automate',
    description:
      'AI agents that answer calls, qualify leads, and book the appointment — 24/7.',
  },
  {
    icon: HubRoundedIcon,
    title: 'Operate',
    description:
      'Quoting, invoicing, scheduling and reporting in one place, connected to your phone, calendar, and Stripe.',
  },
];

const CONCIERGE_TEASERS = [
  {
    icon: SupportAgentRoundedIcon,
    title: '24/7 AI Receptionist',
    description:
      'Never miss a lead. Built on the same AI core as Artimus (Art) — answers SMS and voice with your business context, your hours, and your service details.',
  },
  {
    icon: EventAvailableRoundedIcon,
    title: 'Booking & Scheduling',
    description:
      'It checks availability, suggests times, and books the appointment — then sends reminders automatically.',
  },
  {
    icon: ReceiptLongRoundedIcon,
    title: 'Quote-to-Cash',
    description:
      'Quotes, invoices, programs and Stripe-powered subscriptions in one connected workflow.',
    accent: 'gold',
  },
];

export default function Home() {
  return (
    <Box>
      <Hero
        title={
          <>
            Custom software.{' '}
            <Box component="span" sx={{ color: brand.blueLight }}>
              Intelligent
            </Box>{' '}
            solutions.
          </>
        }
        subtitle="G&R Nexus designs and ships software that connects your business — from the first phone call to the final invoice."
        primaryCta={{ label: 'Meet Concierge', to: '/concierge' }}
        secondaryCta={{ label: 'Book a 20-min demo', to: '/contact' }}
      />

      <Container maxWidth="lg" sx={{ mt: { xs: -8, md: -10 }, position: 'relative', zIndex: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: `1px solid ${brand.border}`,
            boxShadow: '0 30px 60px -30px rgba(15,23,42,0.25)',
          }}
        >
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {PILLARS.map((p) => (
              <Grid key={p.title} size={{ xs: 12, md: 4 }}>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(30,91,255,0.10)',
                      color: brand.blue,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <p.icon />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: brand.navy }}>
                    {p.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: brand.slate }}>
                    {p.description}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="img"
                src={conciergeAiImage}
                alt="Concierge"
                sx={{
                  width: '100%',
                  maxWidth: { xs: 'min(100%, 440px)', sm: 'min(100%, 520px)' },
                  height: 'auto',
                  mt: { xs: 0, md: 3 },
                  mb: 3,
                  display: 'block',
                  borderRadius: 3,
                  border: `1px solid ${brand.border}`,
                }}
              />
              <Typography variant="h3" sx={{ mb: 2, color: brand.navy }}>
                Your 24/7 AI front desk — built for service businesses.
              </Typography>
              <Typography variant="body1" sx={{ color: brand.slate, mb: 3.5 }}>
                Concierge answers every call and message instantly, qualifies the lead, books the
                appointment, and hands you a clean conversation history. Stop losing jobs to
                voicemail.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={RouterLink}
                  to="/concierge"
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Explore Concierge
                </Button>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  size="large"
                  sx={{ borderColor: brand.border }}
                >
                  Contact us
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={2.5}>
                {CONCIERGE_TEASERS.map((f) => (
                  <Grid key={f.title} size={{ xs: 12, sm: 6 }}>
                    <FeatureCard {...f} />
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3.5,
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: brand.navy,
                      color: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      backgroundImage:
                        'radial-gradient(400px 200px at 100% 0%, rgba(30,91,255,0.45), transparent 60%)',
                    }}
                  >
                    <VerifiedRoundedIcon sx={{ color: brand.gold }} />
                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>
                      Built on real operations
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                      Concierge powers production traffic for live service businesses today —
                      it’s not a demo.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#FFFFFF', borderTop: `1px solid ${brand.border}`, borderBottom: `1px solid ${brand.border}` }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What we build"
            title="Software that connects every part of your business."
          />
          <Grid container spacing={3}>
            {[
              {
                title: 'AI workflows & agents',
                description:
                  'From intake bots to research copilots — designed around your domain, your data, your guardrails.',
              },
              {
                title: 'Operations dashboards',
                description:
                  'Real-time visibility for the metrics you actually run on: leads, conversion, revenue, routes, payroll.',
              },
              {
                title: 'Integrations & automation',
                description:
                  'Twilio, OpenPhone, Stripe, Google Calendar, AWS — wired together so your team stops doing manual handoffs.',
              },
              {
                title: 'Mobile + desktop shells',
                description:
                  'Capacitor and Tauri shells so the same product ships to iOS, Android, and the desktop without rewrites.',
              },
            ].map((c) => (
              <Grid key={c.title} size={{ xs: 12, sm: 6, md: 3 }}>
                <FeatureCard title={c.title} description={c.description} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection />
    </Box>
  );
}
